import { defineComponent as _defineComponent } from 'vue'
import {
  computed, reactive, ref, watch
} from 'vue';
import BCXInfoSection from '@/components/molecules/BCXInfoSection.vue';
import ProjectSelector from '@/views/project-forum/components/sidebar-actions/ProjectSelector.vue';
import { useProjectGuests } from '@/views/project-guests/composables/useProjectGuests';
import { ExternalProjectListEntry } from '@/models/Project';
import BCXValidatedInput from '@/components/molecules/BCXValidatedInput.vue';
import useBCValidators from '@/mixins/useBCValidators';
import useVuelidate from '@vuelidate/core';
import BCXTextArea from '@/components/molecules/forms/BCXTextArea.vue';
import ProjectMinimalListItem from '@/views/project-forum/components/ProjectMinimalListItem.vue';
import ConfirmButtons from '@/components/molecules/ConfirmButtons.vue';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import BCXCheckbox from '@/components/molecules/forms/BCXCheckbox.vue';
import MinusIcon from '@/components/svg/minus-circle.svg?inline.svg';
import CompanyService from '@/services/CompanyService';
import useTriggerLegalTextsPopup from '@/mixins/useTriggerLegalTextsPopup';
import useUser from '@/mixins/useUser';
import { AxiosError } from 'axios';
import getAxiosResponseErrorCode from '@/utils/getAxiosResponseErrorCode';
import useI18n from '@/mixins/useI18n';
import TranslatedBackendErrors from '@/utils/TranslatedBackendErrors';
import BackendErrors from '@/components/login-register/BackendErrors.vue';
import { TranslatedErrorCode } from '@/models/BackendError';


export default /*#__PURE__*/_defineComponent({
  __name: 'AddGuest',
  emits: ['invitation-sent'],
  setup(__props, { emit }) {

const { companyId } = useUser('self');
const { projectList, projectName } = useProjectGuests();
const { emailValidation, required } = useBCValidators();
const { t } = useI18n();
const { setLegalText } = useTriggerLegalTextsPopup();

const isMessageEnlarged = ref(false);
const projectsToAdd = ref<ExternalProjectListEntry[]>([]);
const hasInvited = ref(false);
const isChecked = ref(false);
const isResendButtonDisabled = ref(true);
const errorMessage = ref('');
const errorCodes = ref<(TranslatedErrorCode | string)[]>([]);
const showTerms = () => setLegalText('terms');
const showPrivacy = () => setLegalText('privacy');



const initialFields = {
  firstname: '',
  lastname: '',
  email: '',
  message: ''
};
const fields = reactive({ ...initialFields });
const rules = {
  firstname: { required },
  lastname: { required },
  email: emailValidation
};

const v = useVuelidate(rules, fields);

const addProject = (project: ExternalProjectListEntry) => {
  if (projectsToAdd.value.find(({ id }) => id === project?.id)) return;
  projectsToAdd.value.push(project);
  projectName.value = '';
};

const onSelectProject = (project: ExternalProjectListEntry) => {
  if (!project) return;
  addProject(project);
};

const removeProject = (id: string) => {
  const index = projectsToAdd.value.findIndex((project) => project.id === id);
  if (index >= 0) projectsToAdd.value.splice(index, 1);
};

const hasAnyValue = computed(() => v.value?.lastname?.$model
  || v.value?.firstname?.$model
  || v.value?.lastname?.$model
  || v.value?.email?.$model
  || fields.message
  || projectsToAdd.value.length || isChecked.value);

const canSend = computed(() => v.value?.firstname?.$dirty
  && v.value?.lastname?.$dirty
  && v.value?.email?.$dirty
  && !v.value.$errors?.length
  && projectsToAdd.value.length
  && isChecked.value);
const isFormLocked = computed(() => hasInvited.value && !errorCodes.value.length && !errorMessage.value);

const resetFields = (preserveMessageAndProjects = false) => {
  if (!preserveMessageAndProjects) {
    Object.assign(fields, initialFields);
    projectsToAdd.value = [];
  }
  v.value.firstname.$model = '';
  v.value.email.$model = '';
  v.value.lastname.$model = '';
  isChecked.value = false;
  v.value.$reset();
};

const remainingProjectsList = computed(
  () => projectList.value.filter(
    (project) => !projectsToAdd.value.some(
      (addedProject) => addedProject.id === project.id
    )
  )
);

const hasOpenProjects = computed(() => projectList.value.length > 0);
const hasProjectsAssigned = computed(() => projectsToAdd.value.length > 0);

const projectSelectorPlaceholder = computed(() => {
  if (hasOpenProjects.value && !remainingProjectsList.value.length) {
    return t('project-guests.project-placeholder-all-assigned').toString();
  }
  return t('project-guests.project-placeholder').toString();
});

const projectSelectorHint = computed(() => {
  if (!hasOpenProjects.value && !hasProjectsAssigned.value) {
    return t('project-guests.project-no-open-projects').toString();
  }
  return '';
});

const emailConflictErrorCodes = [
  TranslatedErrorCode.EMAIL_NOT_UNIQUE,
  TranslatedErrorCode.SETTINGS_MAIL_EXISTS
];

const clearEmailConflictErrors = () => {
  const nextErrorCodes = errorCodes.value.filter(
    (code) => !emailConflictErrorCodes.includes(code as TranslatedErrorCode)
  );

  if (nextErrorCodes.length === errorCodes.value.length) return;

  errorCodes.value = nextErrorCodes;

  if (!errorCodes.value.length && !errorMessage.value) {
    hasInvited.value = false;
  }
};

watch(projectsToAdd, (projectsToAdd) => {
  if (!projectsToAdd.length) {
    isChecked.value = false;
  }
}, { deep: true });

watch(() => fields.email, () => {
  clearEmailConflictErrors();
});

const inviteGuests = async () => {
  try {
    const axiosResponse = await CompanyService.inviteCompanyGuest(companyId.value, {
      firstName: v.value?.firstname?.$model ?? '',
      lastName: v.value?.lastname?.$model ?? '',
      email: v.value?.email?.$model ?? '',
      message: fields.message,
      projectList: projectsToAdd.value.map((project) => project.id ?? '')
    });

    if (axiosResponse.status < 400) {
      errorMessage.value = '';
      errorCodes.value = [];
      emit('invitation-sent');
    }
  } catch (err) {
    errorCodes.value = [];

    const error = err as AxiosError;
    if (error.response) {
      const backendErrors = new TranslatedBackendErrors(error.response);
      if (backendErrors.hasErrors()) {
        errorCodes.value = backendErrors.allErrors;
        errorMessage.value = ''; // Clear any existing error message
      } else {
        errorMessage.value = t('project-guests.errors.invite-guest-error', {
          code: getAxiosResponseErrorCode(error) ?? 'UNKNOWN_ERROR',
        }).toString();
      }
    } else {
      // Handle network errors or other errors without a response
      errorMessage.value = t('project-guests.errors.invite-guest-error', {
        code: error.code ?? 'NETWORK_ERROR',
      }).toString();
    }
    isResendButtonDisabled.value = true;
    setTimeout(() => {
      isResendButtonDisabled.value = false;
    }, 5000);
  }
  hasInvited.value = true;
};

const newInvite = () => {
  resetFields(true);
  hasInvited.value = false;
};


return { __sfc: true,companyId, projectList, projectName, emailValidation, required, t, setLegalText, isMessageEnlarged, projectsToAdd, hasInvited, isChecked, isResendButtonDisabled, errorMessage, errorCodes, showTerms, showPrivacy, emit, initialFields, fields, rules, v, addProject, onSelectProject, removeProject, hasAnyValue, canSend, isFormLocked, resetFields, remainingProjectsList, hasOpenProjects, hasProjectsAssigned, projectSelectorPlaceholder, projectSelectorHint, emailConflictErrorCodes, clearEmailConflictErrors, inviteGuests, newInvite, BCXInfoSection, ProjectSelector, BCXValidatedInput, BCXTextArea, ProjectMinimalListItem, ConfirmButtons, BCXMarkdown, BCXCheckbox, MinusIcon, BackendErrors }
}

})